"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheArtisanBehindTheBrush() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="82541" id="family-owned-since-2013" className="bg-muted/30 py-16 md:py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none select-none overflow-hidden">
        <span className="font-sans font-black text-[20rem] lg:text-[30rem] leading-none">2013</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-center">
          
          {}
          <div ref={imageRef} className={`lg:col-span-7 relative z-0 transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative aspect-[4/3] lg:aspect-[4/5] w-full overflow-hidden rounded-sm shadow-xl border border-border/50">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/humberg_family_founders.png" alt="The founders of Humberg Electrical Services standing proudly in Richmond, Texas" className="absolute inset-0 w-full h-full object-cover object-center" />
              {}
              <div className="absolute bottom-6 left-6 bg-primary px-6 py-4 shadow-2xl rounded-sm">
                <p className="text-primary-foreground font-mono text-xs uppercase tracking-widest font-bold">Established Legacy</p>
                <p className="text-white font-sans text-xl font-bold">Trusted for 10+ Years</p>
              </div>
              <div className="absolute inset-0 bg-secondary/5 mix-blend-multiply pointer-events-none"></div>
            </div>
          </div>

          {}
          <div ref={textRef} className={`lg:col-span-6 lg:col-start-7 lg:-ml-16 z-10 relative transition-all duration-1000 delay-200 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="bg-background p-8 sm:p-10 lg:p-14 rounded-sm shadow-2xl border border-border/60 relative overflow-hidden">
              {}
              <div className="absolute top-0 right-0 w-32 h-32 bg-destructive/5 rounded-bl-full pointer-events-none"></div>
              
              <h2 className="font-sans font-extrabold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6 leading-tight tracking-tighter">
                Rooted in Richmond: Our Family Legacy
              </h2>
              
              <div className="space-y-5 text-muted-foreground text-lg leading-relaxed font-sans">
                <p>At Humberg Electrical Services, LLC, we believe that electrical work is about more than just wires and circuits.&nbsp; It's about the safety and comfort of your family and business. Established in 2013 right here in Richmond, TX, our family-owned heritage is the foundation of every service call we make.</p>
                <p>From complex commercial panel upgrades to simple residential lighting installs, our "no job is too big or too small" philosophy ensures that every client across Richmond, Sugar Land, Missouri City, and Houston receives the same level of precision and care. We treat every property as if it were our own.</p>
              </div>

              <div className="mt-10 pt-8 border-t border-border/50">
                <p className="font-sans italic text-xl text-primary mb-3 tracking-tight font-medium">
                  "Quality electrical work isn't just a service; it's our family's promise to yours."
                </p>
                <p className="font-mono text-xs text-foreground font-bold uppercase tracking-[0.2em]">
                  — Founders, Humberg Electrical Services
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}